import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
  createSelector,
} from '@reduxjs/toolkit';
import type {
  ContentRecommendation,
  MasteryState,
  BehaviorPattern,
  ContextualFactor,
  AIError,
} from '@coffeeplexity/shared';
import type {
  FilteredContent,
  ContextualSuggestion,
  AdaptiveFilteringConfig,
  EngagementMetrics,
} from '../../services/adaptiveContentFiltering';
import { adaptiveFilteringService } from '../../services/adaptiveContentFiltering';
import { contentDiscoveryAnalytics } from '../../services/contentDiscoveryAnalytics';
import type { RootState } from '../index';

interface AdaptiveFilteringState {
  // Filtered content
  filteredContent: FilteredContent | null;
  contextualSuggestions: ContextualSuggestion[];

  // Configuration
  config: AdaptiveFilteringConfig;
  userFilters: UserFilterPreferences;

  // Analytics
  engagementMetrics: Record<string, EngagementMetrics>;
  filteringHistory: FilteringHistoryEntry[];

  // A/B Testing
  abTestAssignments: ABTestAssignment[];
  activeExperiments: string[];

  // State management
  loading: {
    filtering: boolean;
    suggestions: boolean;
    analytics: boolean;
    abTesting: boolean;
  };
  error: AIError | null;
  lastUpdated: string | null;
  cacheExpiry: string | null;
}

interface UserFilterPreferences {
  confidenceThreshold: number;
  difficultyRange: { min: number; max: number };
  contentTypes: string[];
  showExplanations: boolean;
  enableAnalytics: boolean;
  enableABTesting: boolean;
  maxRecommendations: number;
}

interface FilteringHistoryEntry {
  id: string;
  timestamp: string;
  filtersApplied: any[];
  contentBefore: number;
  contentAfter: number;
  confidenceScore: number;
  userSatisfaction?: number;
}

interface ABTestAssignment {
  experimentId: string;
  variantId: string;
  assignedAt: string;
  active: boolean;
}

const initialState: AdaptiveFilteringState = {
  filteredContent: null,
  contextualSuggestions: [],
  config: {
    masteryConfidenceThreshold: 0.7,
    engagementWeights: {
      viewTime: 0.25,
      completionRate: 0.3,
      rating: 0.2,
      shareCount: 0.1,
      bookmarkCount: 0.1,
      repeatViews: 0.05,
    },
    contextualFactors: [
      { type: 'time_of_day', weight: 0.1, decayRate: 0.1, maxAge: 24 },
      { type: 'session_length', weight: 0.15, decayRate: 0.05, maxAge: 48 },
      { type: 'recent_activity', weight: 0.2, decayRate: 0.2, maxAge: 72 },
    ],
    adaptationSensitivity: 0.8,
    fallbackStrategy: 'rule_based',
  },
  userFilters: {
    confidenceThreshold: 0.7,
    difficultyRange: { min: 0, max: 10 },
    contentTypes: [],
    showExplanations: true,
    enableAnalytics: true,
    enableABTesting: true,
    maxRecommendations: 20,
  },
  engagementMetrics: {},
  filteringHistory: [],
  abTestAssignments: [],
  activeExperiments: [],
  loading: {
    filtering: false,
    suggestions: false,
    analytics: false,
    abTesting: false,
  },
  error: null,
  lastUpdated: null,
  cacheExpiry: null,
};

// Async thunks
export const applyAdaptiveFiltering = createAsyncThunk(
  'adaptiveFiltering/applyFiltering',
  async (
    {
      userId,
      content,
      masteryStates,
      behaviorPatterns,
      contextualFactors,
      userFilters,
    }: {
      userId: string;
      content: ContentRecommendation[];
      masteryStates: MasteryState[];
      behaviorPatterns: BehaviorPattern[];
      contextualFactors: ContextualFactor[];
      userFilters: UserFilterPreferences;
    },
    { rejectWithValue, getState }
  ) => {
    try {
      const state = getState() as RootState;
      const { config } = state.adaptiveFiltering;

      // Apply mastery-based filtering
      const filteredResult =
        await adaptiveFilteringService.applyMasteryBasedFiltering(
          content,
          masteryStates,
          userFilters.confidenceThreshold
        );

      // Apply engagement-based ranking
      const rankedContent =
        await adaptiveFilteringService.rankContentByEngagement(
          filteredResult.content,
          userId,
          behaviorPatterns
        );

      // Limit to max recommendations
      const finalContent = rankedContent.slice(
        0,
        userFilters.maxRecommendations
      );

      return {
        ...filteredResult,
        content: finalContent,
        userId,
        appliedAt: new Date().toISOString(),
      };
    } catch (error: any) {
      const aiError: AIError = {
        code: 'ADAPTIVE_FILTERING_ERROR',
        message: error.message || 'Failed to apply adaptive filtering',
        recoverable: true,
        fallbackStrategy: 'rule_based',
      };
      return rejectWithValue(aiError);
    }
  }
);

export const generateContextualSuggestions = createAsyncThunk(
  'adaptiveFiltering/generateSuggestions',
  async (
    {
      userId,
      currentContentId,
      contextualFactors,
      availableContent,
    }: {
      userId: string;
      currentContentId: string;
      contextualFactors: ContextualFactor[];
      availableContent: ContentRecommendation[];
    },
    { rejectWithValue }
  ) => {
    try {
      const suggestions =
        await adaptiveFilteringService.generateContextualSuggestions(
          currentContentId,
          userId,
          contextualFactors,
          availableContent
        );

      return suggestions;
    } catch (error: any) {
      const aiError: AIError = {
        code: 'CONTEXTUAL_SUGGESTIONS_ERROR',
        message: error.message || 'Failed to generate contextual suggestions',
        recoverable: true,
        fallbackStrategy: 'rule_based',
      };
      return rejectWithValue(aiError);
    }
  }
);

export const updateEngagementMetrics = createAsyncThunk(
  'adaptiveFiltering/updateEngagement',
  async (
    {
      contentId,
      metrics,
    }: {
      contentId: string;
      metrics: Partial<EngagementMetrics>;
    },
    { rejectWithValue }
  ) => {
    try {
      adaptiveFilteringService.updateEngagementMetrics(contentId, metrics);

      return {
        contentId,
        metrics: {
          ...metrics,
          lastUpdated: new Date().toISOString(),
        },
      };
    } catch (error: any) {
      const aiError: AIError = {
        code: 'ENGAGEMENT_UPDATE_ERROR',
        message: error.message || 'Failed to update engagement metrics',
        recoverable: true,
        fallbackStrategy: 'rule_based',
      };
      return rejectWithValue(aiError);
    }
  }
);

export const trackFilteringAnalytics = createAsyncThunk(
  'adaptiveFiltering/trackAnalytics',
  async (
    {
      userId,
      eventType,
      eventData,
      context,
    }: {
      userId: string;
      eventType: string;
      eventData: Record<string, any>;
      context: any;
    },
    { rejectWithValue }
  ) => {
    try {
      contentDiscoveryAnalytics.trackEvent({
        userId,
        eventType,
        sessionId: `session_${userId}_${Date.now()}`,
        context,
        metadata: eventData,
      });

      return {
        eventType,
        timestamp: new Date().toISOString(),
        success: true,
      };
    } catch (error: any) {
      const aiError: AIError = {
        code: 'ANALYTICS_TRACKING_ERROR',
        message: error.message || 'Failed to track analytics event',
        recoverable: true,
        fallbackStrategy: 'rule_based',
      };
      return rejectWithValue(aiError);
    }
  }
);

const adaptiveFilteringSlice = createSlice({
  name: 'adaptiveFiltering',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null;
    },

    clearFilteredContent: state => {
      state.filteredContent = null;
      state.contextualSuggestions = [];
      state.lastUpdated = null;
      state.cacheExpiry = null;
    },

    updateUserFilters: (
      state,
      action: PayloadAction<Partial<UserFilterPreferences>>
    ) => {
      state.userFilters = { ...state.userFilters, ...action.payload };
    },

    updateConfig: (
      state,
      action: PayloadAction<Partial<AdaptiveFilteringConfig>>
    ) => {
      state.config = { ...state.config, ...action.payload };
    },

    addFilteringHistoryEntry: (
      state,
      action: PayloadAction<Omit<FilteringHistoryEntry, 'id'>>
    ) => {
      const entry: FilteringHistoryEntry = {
        ...action.payload,
        id: `history_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      };

      state.filteringHistory.unshift(entry);

      // Keep only the most recent 50 entries
      if (state.filteringHistory.length > 50) {
        state.filteringHistory = state.filteringHistory.slice(0, 50);
      }
    },

    updateABTestAssignments: (
      state,
      action: PayloadAction<ABTestAssignment[]>
    ) => {
      state.abTestAssignments = action.payload;
      state.activeExperiments = action.payload
        .filter(a => a.active)
        .map(a => a.experimentId);
    },

    markSuggestionUsed: (state, action: PayloadAction<string>) => {
      const suggestion = state.contextualSuggestions.find(
        s => s.id === action.payload
      );
      if (suggestion) {
        suggestion.estimatedRelevance = Math.min(
          1,
          suggestion.estimatedRelevance + 0.1
        );
      }
    },

    updateEngagementMetric: (
      state,
      action: PayloadAction<{
        contentId: string;
        metrics: Partial<EngagementMetrics>;
      }>
    ) => {
      const { contentId, metrics } = action.payload;
      state.engagementMetrics[contentId] = {
        ...state.engagementMetrics[contentId],
        ...metrics,
        lastUpdated: new Date().toISOString(),
      } as EngagementMetrics;
    },

    setCacheExpiry: (state, action: PayloadAction<string>) => {
      state.cacheExpiry = action.payload;
    },

    resetFilters: state => {
      state.userFilters = initialState.userFilters;
    },
  },

  extraReducers: builder => {
    // Apply Adaptive Filtering
    builder
      .addCase(applyAdaptiveFiltering.pending, state => {
        state.loading.filtering = true;
        state.error = null;
      })
      .addCase(applyAdaptiveFiltering.fulfilled, (state, action) => {
        state.loading.filtering = false;
        state.filteredContent = action.payload;
        state.lastUpdated = new Date().toISOString();
        state.cacheExpiry = new Date(Date.now() + 15 * 60 * 1000).toISOString(); // 15 minutes

        // Add to history
        if (action.payload.appliedFilters.length > 0) {
          const historyEntry: FilteringHistoryEntry = {
            id: `history_${Date.now()}`,
            timestamp: new Date().toISOString(),
            filtersApplied: action.payload.appliedFilters,
            contentBefore: 0, // Would be calculated from original content
            contentAfter: action.payload.content.length,
            confidenceScore: action.payload.confidenceScore,
          };

          state.filteringHistory.unshift(historyEntry);
          if (state.filteringHistory.length > 50) {
            state.filteringHistory = state.filteringHistory.slice(0, 50);
          }
        }
      })
      .addCase(applyAdaptiveFiltering.rejected, (state, action) => {
        state.loading.filtering = false;
        state.error = action.payload as AIError;
      });

    // Generate Contextual Suggestions
    builder
      .addCase(generateContextualSuggestions.pending, state => {
        state.loading.suggestions = true;
      })
      .addCase(generateContextualSuggestions.fulfilled, (state, action) => {
        state.loading.suggestions = false;
        state.contextualSuggestions = action.payload;
      })
      .addCase(generateContextualSuggestions.rejected, (state, action) => {
        state.loading.suggestions = false;
        state.error = action.payload as AIError;
      });

    // Update Engagement Metrics
    builder
      .addCase(updateEngagementMetrics.pending, state => {
        state.loading.analytics = true;
      })
      .addCase(updateEngagementMetrics.fulfilled, (state, action) => {
        state.loading.analytics = false;
        const { contentId, metrics } = action.payload;
        state.engagementMetrics[contentId] = {
          ...state.engagementMetrics[contentId],
          ...metrics,
        } as EngagementMetrics;
      })
      .addCase(updateEngagementMetrics.rejected, (state, action) => {
        state.loading.analytics = false;
        state.error = action.payload as AIError;
      });

    // Track Analytics
    builder
      .addCase(trackFilteringAnalytics.pending, state => {
        state.loading.analytics = true;
      })
      .addCase(trackFilteringAnalytics.fulfilled, state => {
        state.loading.analytics = false;
      })
      .addCase(trackFilteringAnalytics.rejected, (state, action) => {
        state.loading.analytics = false;
        state.error = action.payload as AIError;
      });
  },
});

export const {
  clearError,
  clearFilteredContent,
  updateUserFilters,
  updateConfig,
  addFilteringHistoryEntry,
  updateABTestAssignments,
  markSuggestionUsed,
  updateEngagementMetric,
  setCacheExpiry,
  resetFilters,
} = adaptiveFilteringSlice.actions;

// Selectors
export const selectFilteredContent = (state: RootState) =>
  state.adaptiveFiltering.filteredContent;

export const selectContextualSuggestions = (state: RootState) =>
  state.adaptiveFiltering.contextualSuggestions;

export const selectUserFilters = (state: RootState) =>
  state.adaptiveFiltering.userFilters;

export const selectFilteringConfig = (state: RootState) =>
  state.adaptiveFiltering.config;

export const selectEngagementMetrics = (state: RootState) =>
  state.adaptiveFiltering.engagementMetrics;

export const selectFilteringHistory = (state: RootState) =>
  state.adaptiveFiltering.filteringHistory;

export const selectABTestAssignments = (state: RootState) =>
  state.adaptiveFiltering.abTestAssignments;

export const selectAdaptiveFilteringLoading = (state: RootState) =>
  state.adaptiveFiltering.loading;

export const selectAdaptiveFilteringError = (state: RootState) =>
  state.adaptiveFiltering.error;

// Memoized selectors
export const selectHighConfidenceSuggestions = createSelector(
  [selectContextualSuggestions],
  suggestions => suggestions.filter(s => s.confidence >= 0.7)
);

export const selectRecentFilteringHistory = createSelector(
  [selectFilteringHistory],
  history => {
    const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    return history.filter(entry => new Date(entry.timestamp) > oneWeekAgo);
  }
);

export const selectFilteringEffectiveness = createSelector(
  [selectFilteringHistory],
  history => {
    if (history.length === 0) return 0;

    const avgConfidence =
      history.reduce((sum, entry) => sum + entry.confidenceScore, 0) /
      history.length;
    const avgSatisfaction =
      history
        .filter(entry => entry.userSatisfaction !== undefined)
        .reduce((sum, entry) => sum + (entry.userSatisfaction || 0), 0) /
      Math.max(
        1,
        history.filter(entry => entry.userSatisfaction !== undefined).length
      );

    return (avgConfidence + avgSatisfaction) / 2;
  }
);

export const selectActiveABTests = createSelector(
  [selectABTestAssignments],
  assignments => assignments.filter(a => a.active)
);

export const selectTopEngagingContent = createSelector(
  [selectEngagementMetrics],
  metrics => {
    return Object.entries(metrics)
      .sort(([, a], [, b]) => b.engagementScore - a.engagementScore)
      .slice(0, 10)
      .map(([contentId, metric]) => ({ contentId, ...metric }));
  }
);

export const selectIsFilteringCacheStale = createSelector(
  [
    selectFilteredContent,
    (state: RootState) => state.adaptiveFiltering.cacheExpiry,
  ],
  (filteredContent, cacheExpiry) => {
    if (!filteredContent || !cacheExpiry) return true;
    return new Date() > new Date(cacheExpiry);
  }
);

export default adaptiveFilteringSlice.reducer;
